/* <card-swatches> — accurate variant-image swatches for product cards.
   No external libraries. Delegated listeners. Reuses the theme's card image.
   Scoped to product cards; does not touch the PDP picker or cart upsell. */
(function () {
  const MOBILE_QUERY = '(max-width: 749px)';

  class CardSwatches extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.cardWrapper = this.closest('.card-wrapper');
      this.list = this.querySelector('.card-swatches__list');
      this.swatches = Array.from(this.querySelectorAll('.card-swatch'));
      this.labelEl = this.querySelector('[data-swatch-label]');
      this.moreEl = this.querySelector('[data-swatch-more]');
      this.moreCountEl = this.querySelector('[data-swatch-more-count]');

      if (!this.cardWrapper || this.swatches.length < 2) return;

      this.total = parseInt(this.dataset.total, 10) || this.swatches.length;
      this.maxDesktop = parseInt(this.dataset.maxDesktop, 10) || 4;
      this.maxMobile = parseInt(this.dataset.maxMobile, 10) || 3;
      this.showLabel = this.dataset.showLabel === 'true';
      this.hoverPreview = this.dataset.hoverPreview !== 'false';
      this.persistSelection = this.dataset.persistSelection !== 'false';

      this.selected = null;
      this.captureBaseImage();
      this.baselinePrice = this.swatches[0]
        ? parseInt(this.swatches[0].dataset.price, 10)
        : null;
      this.uniformPrice = this.swatches.every(
        (s) => parseInt(s.dataset.price, 10) === this.baselinePrice
      );

      this.bindEvents();

      this.mql = window.matchMedia(MOBILE_QUERY);
      this.onBreakpoint = this.applyTruncation.bind(this);
      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this.onBreakpoint);
      } else {
        this.mql.addListener(this.onBreakpoint);
      }
      this.applyTruncation();
    }

    captureBaseImage() {
      this.imageEl = this.cardWrapper.querySelector(
        '.card--image-animate.media img'
      );
      this.sourceEl = this.cardWrapper.querySelector(
        '.card--image-animate.media source'
      );
      if (this.imageEl) {
        this.baseSrc = this.imageEl.getAttribute('src');
        this.baseSrcset = this.imageEl.getAttribute('srcset');
        this.baseAlt = this.imageEl.getAttribute('alt') || '';
      }
      if (this.sourceEl) {
        this.baseSourceSrcset = this.sourceEl.getAttribute('srcset');
      }
    }

    bindEvents() {
      this.list.addEventListener('pointerenter', this.onEnter.bind(this), true);
      this.list.addEventListener('pointerleave', this.onLeave.bind(this), true);
      this.list.addEventListener('focusin', this.onEnter.bind(this));
      this.list.addEventListener('focusout', this.onLeave.bind(this));
      this.list.addEventListener('click', this.onClick.bind(this));
      this.list.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.moreEl) {
        this.moreEl.addEventListener('click', this.onMoreClick.bind(this));
      }
    }

    swatchFromEvent(event) {
      const el = event.target.closest('.card-swatch');
      return el && this.contains(el) ? el : null;
    }

    onEnter(event) {
      if (!this.hoverPreview) return;
      const swatch = this.swatchFromEvent(event);
      if (!swatch) return;
      this.preview(swatch);
    }

    onLeave(event) {
      if (!this.hoverPreview) return;
      const swatch = this.swatchFromEvent(event);
      if (!swatch) return;
      // Ignore focusout that stays within the same swatch subtree.
      if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
      this.restore();
    }

    onClick(event) {
      const swatch = this.swatchFromEvent(event);
      if (!swatch) return;
      event.preventDefault();
      this.select(swatch);
    }

    onKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      const focusable = this.swatches.filter((s) => !s.hidden);
      const idx = focusable.indexOf(document.activeElement);
      if (idx === -1) return;
      event.preventDefault();
      const next =
        event.key === 'ArrowRight'
          ? focusable[idx + 1] || focusable[0]
          : focusable[idx - 1] || focusable[focusable.length - 1];
      next.focus();
    }

    onMoreClick(event) {
      // Prefer opening the card's Choose Options / Quick View. If none exists,
      // fall through to the anchor's default navigation to the product page.
      const quickView =
        this.cardWrapper.querySelector('.quick-view__summary') ||
        this.cardWrapper.querySelector('quick-view-button');
      if (quickView) {
        event.preventDefault();
        quickView.click();
      }
    }

    setImage(src, srcset, colorName) {
      if (this.imageEl && src) {
        this.imageEl.src = src;
        if (srcset) this.imageEl.srcset = srcset;
        if (colorName && this.baseAlt) {
          this.imageEl.alt = this.baseAlt + ' \u2013 ' + colorName;
        }
      }
      if (this.sourceEl && srcset) this.sourceEl.srcset = srcset;
    }

    preview(swatch) {
      const src = swatch.dataset.mediaSrc;
      const srcset = swatch.dataset.mediaSrcset;
      if (src) {
        this.cardWrapper.classList.add('atmd-swatch-active');
        this.setImage(src, srcset, swatch.dataset.colorName);
      }
      this.setLabel(swatch.dataset.colorName);
    }

    restore() {
      if (this.selected) {
        this.preview(this.selected);
        return;
      }
      if (this.imageEl) this.setImage(this.baseSrc, this.baseSrcset, null);
      if (this.imageEl && this.baseAlt) this.imageEl.alt = this.baseAlt;
      this.cardWrapper.classList.remove('atmd-swatch-active');
      this.setLabel('');
    }

    select(swatch) {
      if (this.persistSelection) {
        this.selected = swatch;
        this.swatches.forEach((s) =>
          s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false')
        );
        this.cardWrapper.classList.add('atmd-swatch-active');
      }
      this.preview(swatch);
      this.updateLinks(swatch);
      this.updatePrice(swatch);
    }

    updateLinks(swatch) {
      const url = swatch.dataset.variantUrl;
      if (!url) return;
      this.cardWrapper
        .querySelectorAll(
          'a.card__media, a.full-unstyled-link, a.card-information__text, .card-information__button a, .card__inner > a'
        )
        .forEach((a) => {
          if (a.hasAttribute('href')) a.setAttribute('href', url);
        });
    }

    updatePrice(swatch) {
      if (this.uniformPrice) return;
      const price = parseInt(swatch.dataset.price, 10);
      if (!price || price === this.baselinePrice) return;
      try {
        if (!window.theme || !theme.Currency) return;
        const priceEl = this.cardWrapper.querySelector(
          '.price__regular .price-item--regular'
        );
        if (!priceEl) return;
        priceEl.innerHTML =
          '<price-money><bdi>' +
          theme.Currency.formatMoney(price, theme.shopSettings.moneyFormat) +
          '</bdi></price-money>';
      } catch (e) {
        /* keep original price on any failure */
      }
    }

    setLabel(text) {
      if (this.showLabel && this.labelEl) this.labelEl.textContent = text || '';
    }

    applyTruncation() {
      const max = this.mql.matches ? this.maxMobile : this.maxDesktop;
      let visibleCount = 0;
      this.swatches.forEach((swatch, index) => {
        const hide = index >= max;
        swatch.hidden = hide;
        if (!hide) visibleCount += 1;
      });

      if (!this.moreEl) return;
      // Total colours minus the ones actually shown in this breakpoint. Overflow
      // colours beyond the rendered set are already counted in `this.total`.
      const remaining = this.total - visibleCount;
      if (remaining > 0) {
        this.moreEl.hidden = false;
        this.moreEl.setAttribute(
          'aria-label',
          'View ' + remaining + ' additional color' + (remaining === 1 ? '' : 's')
        );
        this.moreEl.setAttribute('title', remaining + ' more color' + (remaining === 1 ? '' : 's'));
        if (this.moreCountEl) this.moreCountEl.textContent = '+' + remaining;
      } else {
        this.moreEl.hidden = true;
      }
    }
  }

  if (!customElements.get('card-swatches')) {
    customElements.define('card-swatches', CardSwatches);
  }
})();
